import { orderSlice } from 'Flux/Slice/Order/OrderSlice.js'
import { useProperty } from 'Framework/View/index.js'
import { Box, FormControl, Input, Pagination, Table } from 'View/Common/index.js'
import type { ChangeEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Actions, { useDispatch } from 'Flux/index.js'
import { debounceTime } from 'rxjs'
import { useGetAllOrders, useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { Link, useSearchParams } from 'react-router-dom'
import type { IOrder, IOrderWithCounts } from 'Model/index.js'
import { useInteraction } from 'Framework/View/Hooks/useInteraction.js'
import { UserAndOrgWidget } from './UserAndOrgWidget.js'
import { DateWidget } from '../../Common/DateWidget.js'
import { determineOrderStateForList } from 'Util/OrderState.js'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import { AmpliconWidget } from './AmpliconWidget.tsx'
import { useSelector } from 'react-redux'
import type { State } from '../../../Flux/Slice/index.js'
import { organizationSlice } from '../../../Flux/Slice/Organization/OrganizationSlice.js'
import type { FilterTypeKey } from '../../../Generated/FilterType.js'
import type { SearchFilter } from '../../../Generated/SearchFilter.js'
import { orderToStateWidget } from './OrderStateWidget.js'
import { getProcessingTypeAccentBg, getProcessingTypeLabel } from '../../../Util/processingType.ts'
import { TooltipTrigger } from '../../../Provider/TooltipManager.tsx'
import { trackOrderListView } from '../../../Services/Telemetry/index.js'
import type { OrderListUrlState } from './orderListUrlState.ts'
import { pageSizes, parseOrderListUrlState, serializeOrderListUrlState } from './orderListUrlState.ts'

export const takeOptions = pageSizes.map((n) => ({ value: String(n), label: String(n) }))

function buildOrderFilters(f: OrderListUrlState): SearchFilter[] {
  const filters: SearchFilter[] = [
    {
      value: '',
      parameter: '',
      filterType: 'OR' as FilterTypeKey,
      leftOperand: { value: f.search, parameter: 'name', filterType: 'SUBSTR' as FilterTypeKey },
      rightOperand: { value: f.search, parameter: 'description', filterType: 'SUBSTR' as FilterTypeKey },
    },
  ]
  if (f.onlyCompleted) {
    filters.push({ value: '', parameter: 'orderDone', filterType: 'NOT_NULL' as FilterTypeKey })
  }
  if (f.onlySubmitted) {
    filters.push({ value: '', parameter: 'orderSubmission', filterType: 'NOT_NULL' as FilterTypeKey })
  }
  if (f.taxonomy === '') {
    filters.push({ value: '', parameter: 'targetTaxonomy', filterType: 'IS_NULL' as FilterTypeKey })
  } else if (f.taxonomy) {
    filters.push({ value: f.taxonomy, parameter: 'targetTaxonomy', filterType: 'SUBSTR' as FilterTypeKey })
  }
  if (f.processingType) {
    filters.push({ value: f.processingType, parameter: 'processingType', filterType: 'SUBSTR' as FilterTypeKey })
  }
  if (f.orgId) {
    filters.push({
      value: '',
      parameter: '',
      filterType: 'OR' as FilterTypeKey,
      leftOperand: { value: f.orgId, parameter: 'lab', filterType: 'EQUAL' as FilterTypeKey },
      rightOperand: { value: f.orgId, parameter: 'customer', filterType: 'EQUAL' as FilterTypeKey },
    })
  }
  return filters
}

const CountBadge = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 rounded-full bg-gray-100 text-gray-700 text-sm font-medium px-1.5">
    {children}
  </span>
)

export const OrderManagementPage = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(Actions[orderSlice.name].resetOrder())
  }, [])

  const [searchParams, setSearchParams] = useSearchParams()
  const urlState = useMemo(() => parseOrderListUrlState(searchParams), [searchParams])
  const { page, size: take } = urlState
  const search$ = useProperty<string>(urlState.search)
  const lastWrittenSearch = useRef(urlState.search)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Navigations commit in a transition and setSearchParams(fn) sees the last committed params, so two
  // changes before the commit would drop the first. Resync only when the committed params change.
  const pendingUrlState = useRef(urlState)
  const committedParams = useRef(searchParams)
  if (committedParams.current !== searchParams) {
    committedParams.current = searchParams
    pendingUrlState.current = urlState
  }

  const updateUrlState = useCallback(
    (patch: Partial<OrderListUrlState> | ((current: OrderListUrlState) => Partial<OrderListUrlState>), replace = false) => {
      const current = pendingUrlState.current
      const next = { ...current, page: 1, ...(typeof patch === 'function' ? patch(current) : patch) }
      pendingUrlState.current = next
      setSearchParams(serializeOrderListUrlState(next), { replace })
    },
    [setSearchParams]
  )

  const authenticatedUser = useGetAuthenticatedUser()
  const { orders, total } = useGetAllOrders()

  const onPagination$ = useInteraction<number>()
  const onDownloadFiles$ = useInteraction<IOrderWithCounts>()

  const relatedOrgs = useSelector((state: typeof State) => state.organization.organizations)

  // The slice keys organizations by id, so `Object.values` hands them back in whatever order they
  // were fetched -- unusable as a picker once an account is related to more than a handful.
  const orgsByName = useMemo(
    () => Object.values(relatedOrgs).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [relatedOrgs]
  )

  const handleSelectFilter =
    (key: 'taxonomy' | 'processingType' | 'orgId') => (e: ChangeEvent<HTMLSelectElement>) => {
      updateUrlState({ [key]: e.target.value === 'NO_FILTER' ? null : e.target.value })
    }

  useEffect(() => {
    const onPagination$$ = onPagination$.subscribe((next) => updateUrlState({ page: next }))
    return () => onPagination$$.unsubscribe()
  }, [onPagination$, updateUrlState])

  useEffect(() => {
    const search$$ = search$.pipe(debounceTime(400)).subscribe((value) => {
      if (value === lastWrittenSearch.current) {
        return
      }
      lastWrittenSearch.current = value
      updateUrlState({ search: value }, true)
    })
    return () => search$$.unsubscribe()
  }, [search$, updateUrlState])

  useEffect(() => {
    // `q` changed from outside the box: back/forward or a link.
    if (urlState.search !== lastWrittenSearch.current) {
      lastWrittenSearch.current = urlState.search
      search$.next(urlState.search)
    }
  }, [urlState.search, search$])

  useEffect(() => {
    if (authenticatedUser) {
      dispatch(Actions[organizationSlice.name].getRelatedOrganizationsRequest({}))
    }
  }, [authenticatedUser])

  useEffect(() => {
    const onDownloadFiles$$ = onDownloadFiles$.subscribe((data) => {
      dispatch(Actions[orderSlice.name].getFastqRequest({ orderId: data.order.id }))
    })
    return () => onDownloadFiles$$.unsubscribe()
  }, [onDownloadFiles$])

  useEffect(() => {
    dispatch(
      Actions[orderSlice.name].getAllOrderRequest({
        size: urlState.size,
        page: urlState.page - 1,
        filters: buildOrderFilters(urlState),
      })
    )
  }, [dispatch, urlState])

  // ---------------------------------------------------------------------------
  // Track order list view (fires once per fetched result set, with the customer/lab role split)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (orders.length === 0 && total === 0) return // skip the pre-load reset / not-yet-loaded state
    trackOrderListView(orders.map((o) => o.order), total)
  }, [orders, total])

  const downloadReport = (order: IOrder) => {
    dispatch(
      Actions[orderSlice.name].getReportRequest({
        orderName: order.name,
        orderId: order.id,
      })
    )
  }

  if (!authenticatedUser) {
    return <div>Unauthorized</div>
  }

  return (
    <>
      <Box title="Orders">
        <button
          className="block md:hidden w-full btn btn-sm mb-3"
          onClick={() => setShowMobileFilters((prev) => !prev)}
        >
          {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
        </button>

        <div className={`${showMobileFilters ? 'block' : 'hidden'} md:block mb-4 space-y-3`}>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <FormControl>
                <Input type="text" onChange$={search$} placeholder="Search by name or description" />
              </FormControl>
            </div>
            <div className="flex-shrink-0">
              <label htmlFor="page-size-select" className="block text-xs font-medium text-gray-500 mb-1 text-center">
                Per page
              </label>
              <select
                id="page-size-select"
                className="select"
                value={String(take)}
                onChange={(e) => updateUrlState({ size: parseInt(e.target.value) })}
              >
                {takeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label htmlFor="taxon-filter" className="block text-xs font-medium text-gray-500 mb-1">
                Taxon
              </label>
              <select
                id="taxon-filter"
                className="select"
                value={urlState.taxonomy ?? 'NO_FILTER'}
                onChange={handleSelectFilter('taxonomy')}
              >
                <option value="NO_FILTER">All taxa</option>
                <option value="">None (unset)</option>
                {taxaOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="processing-filter" className="block text-xs font-medium text-gray-500 mb-1">
                Processing
              </label>
              <select
                id="processing-filter"
                className="select"
                value={urlState.processingType ?? 'NO_FILTER'}
                onChange={handleSelectFilter('processingType')}
              >
                <option value="NO_FILTER">All types</option>
                <option value="METABARCODING">Metabarcoding</option>
                <option value="EDNA">eDNA</option>
              </select>
            </div>

            <div>
              <label htmlFor="org-filter" className="block text-xs font-medium text-gray-500 mb-1">
                Organization
              </label>
              <select
                id="org-filter"
                className="select"
                value={urlState.orgId ?? 'NO_FILTER'}
                onChange={handleSelectFilter('orgId')}
              >
                <option value="NO_FILTER">All organizations</option>
                {orgsByName.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-4 items-center pb-1">
              <label htmlFor="toggleOnlyCompleted" className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  className="checkbox"
                  type="checkbox"
                  id="toggleOnlyCompleted"
                  checked={urlState.onlyCompleted}
                  onChange={() => updateUrlState((current) => ({ onlyCompleted: !current.onlyCompleted }))}
                  data-testid="showOnlyCompletedCheckbox"
                />
                Completed only
              </label>
              <label htmlFor="toggleOnlySubmitted" className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  className="checkbox"
                  type="checkbox"
                  id="toggleOnlySubmitted"
                  checked={urlState.onlySubmitted}
                  onChange={() => updateUrlState((current) => ({ onlySubmitted: !current.onlySubmitted }))}
                  data-testid="showOnlySubmittedCheckbox"
                />
                Submitted only
              </label>
            </div>
          </div>
        </div>

        <Table type="table-auto">
          <Table.Head className="bg-gray-50 border-b-2 border-gray-200">
            <Table.Row>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap py-1">Name</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">State</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Taxon</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Pools</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Samples</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">NC</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Customer</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Lab</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Created</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Report</div></Table.Cell>
              <Table.Cell><div className="text-xs font-semibold uppercase tracking-wide text-gray-500 text-center whitespace-nowrap py-1">Files</div></Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {orders?.map((item) => (
              <Table.Row key={item.order.id} className="hover:bg-blue-50/40 transition-colors">
                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="max-w-[280px]">
                      <div className="flex items-center gap-2">
                        <TooltipTrigger tooltip={getProcessingTypeLabel(item.order.processingType)}>
                          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${getProcessingTypeAccentBg(item.order.processingType)}`} />
                        </TooltipTrigger>
                        <span className="font-medium text-sm">{item.order.name}</span>
                      </div>
                      {item.order.description && (
                        <div className="mt-0.5 text-xs text-gray-400 whitespace-pre-wrap">{item.order.description}</div>
                      )}
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="flex justify-center">
                      {orderToStateWidget(determineOrderStateForList(item.order, item.hasBlockingItems), null)}
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="flex justify-center">
                      <AmpliconWidget amplicon={item.order.targetTaxonomy} showLabel={false} classNames="text-xl" />
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="flex justify-center">
                      <CountBadge>{item.poolCount}</CountBadge>
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="flex justify-center">
                      <CountBadge>{item.sampleCount}</CountBadge>
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <Link to={`/order/${item.order.id}`}>
                    <div className="flex justify-center">
                      <CountBadge>{item.negativeControlCount}</CountBadge>
                    </div>
                  </Link>
                </Table.Cell>

                <Table.Cell>
                  <UserAndOrgWidget
                    id={item.order.customer}
                    org={item.order.customerOrg}
                    userId={item.order.draftCreatedAt ? item.order.createdByUser : item.order.claimedByUser}
                  />
                </Table.Cell>

                <Table.Cell>
                  <UserAndOrgWidget
                    id={item.order.lab}
                    org={item.order.labOrg}
                    userId={item.order.draftCreatedAt ? item.order.claimedByUser : item.order.createdByUser}
                  />
                </Table.Cell>

                <Table.Cell>
                  <div className="flex justify-center text-sm text-gray-600 whitespace-nowrap">
                    {item.order.draftCreatedAt ? (
                      <DateWidget date={item.order.draftCreatedAt} />
                    ) : (
                      item.order.orderCreated && <DateWidget date={item.order.orderCreated} />
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <div className="flex justify-center">
                    {item.order.orderDone && (
                      <button
                        onClick={() => downloadReport(item.order)}
                        className="text-sm text-primary hover:underline font-medium"
                      >
                        Download
                      </button>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <div className="flex justify-center">
                    <button
                      className="text-sm text-primary hover:underline font-medium"
                      onClick={() => onDownloadFiles$.next(item)}
                    >
                      Files
                    </button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <Pagination onClick$={onPagination$} page={page} total={total} take={take} />
      </Box>
    </>
  )
}
